import { styled } from '@mui/material';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';

import { CARD_WIDTH } from '@/components/ui/swimlane/layout/constants';

// some styles are in SelectableCard.tsx since they rely on the hover state of the card
const MarqueeTitle = styled('div', {
  shouldForwardProp: (prop: string) => prop !== 'isCompact',
})<{ isCompact: boolean }>(
  ({ isCompact }) => `
  text-transform: capitalize;
  font-weight: bold;
  line-height: 20px;
  color: #2B343C;
  overflow: hidden;
  width: 100%;
  &:hover .rfm-child {
    width: '100%';
  };
  .rfm-child {
    width: ${isCompact ? 197 : 297}px;
    white-space: nowrap;
  }
`,
);

const DELIMITER_WIDTH = 20;

interface CustomHTMLAttributes<T> extends React.HTMLAttributes<T> {
  [key: `data-${string}`]: string | undefined;
}

interface ICardTitleProps {
  isCompact: boolean;
  fontSize: number;
  children: React.ReactNode;
  textProps?: CustomHTMLAttributes<HTMLDivElement>;
}

export function CardTitle({ isCompact, fontSize, children, textProps = {} }: ICardTitleProps) {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const [hasFocus, setHasFocus] = useState(false);

  const canToggleScroll = useCallback(() => {
    if (marqueeRef.current && !hasFocus) {
      const title = marqueeRef.current.firstElementChild as HTMLElement;
      const titleWidth = title.clientWidth - DELIMITER_WIDTH;
      const containerWidth = marqueeRef.current.parentElement?.offsetWidth || CARD_WIDTH.value;

      return titleWidth > containerWidth && setHasFocus(true);
    }
  }, [marqueeRef, hasFocus]);

  const mouseLeave = useCallback(() => {
    setHasFocus(false);
    if (marqueeRef.current) {
      const marqueeContainer = marqueeRef.current as HTMLElement;
      const marqueeChildren = Array.from(marqueeContainer.children) as HTMLElement[];

      marqueeChildren.forEach((marquee: HTMLElement) => {
        marquee.style.animation = 'none';
        marquee.offsetHeight; // accessing the property is necessary to trigger the reflow
        marquee.style.animation = '';
      });
    }
  }, [marqueeRef]);

  return (
    <MarqueeTitle
      isCompact={isCompact}
      role="heading"
      style={{ fontSize: `${fontSize}px` }}
      onMouseEnter={canToggleScroll}
      onMouseLeave={mouseLeave}
    >
      <Marquee ref={marqueeRef} play={hasFocus}>
        <div
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
          {...textProps}
        >
          {children}
        </div>
      </Marquee>
    </MarqueeTitle>
  );
}
